import { EonUiForm, EonUiPreloaderCircle } from "@eon-ui/eon-ui-components-react";
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "@me8eon/translation";
import { TileDataInterface } from "@me8eon/fav_storage";
import "./SearchList.scss";
import ListItem from "./ListItem";
import useClickOutside from "../../util/useClickOutside";
import useSearchStore, { newAiQue, que } from "../../store/searchStore";
import useOutsideFocus from "../../util/useOutsideFocus";
import { useFeedback } from "@me8eon/feedback";
import { useSelectedDataViewName } from "@me8eon/data_view_filter";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import AutoResizingTextarea from './AutoResizingTextarea';
import { SearchTextFilter, useSearchTextFilter } from "@me8eon/searchtext_filters";
import { useFeatureFlag } from "@me8eon/featureflags";
import { AiAssistContext } from "../ai-assist-refactored/AiAssist.context";
export interface Questionator {
    title: string;
}

export const SearchList = <Filters extends SearchTextFilter>({
    source,
    itemClickedCallback,
    queryChange,
    limitedHeight,
    listReverse,
    defaultInputVal,
    disabledSearch,
    indices,
    placeholder,
    enableGrow,
}: {
    source?: "ai-assist" | "all-apps";
    itemClickedCallback?: (query: string) => void;
    queryChange?: (query: string) => void;
    limitedHeight?: boolean;
    listReverse?: boolean;
    defaultInputVal?: null | string;
    disabledSearch?: boolean;
    indices?: string[];
    placeholder?: string;
    enableGrow?: boolean;
    showAiAssist?: boolean;
}) => {
    const {
        loading,
        isEditMode,
        isSearchFocused,
        setInputFocused,
        fetchAdvanceSearchData,
        resultUpdate,
        questionairData,
        getAllApps,
    } = useSearchStore();
    const location = useLocation();
    const { currentRole } = useUserData();
    const isAiPageAlready = useMemo(() => location.pathname === "/ai", [location])
    const { t, i18n } = useTranslation();
    const [query, setQuery] = useSearchTextFilter();
    const navigate = useNavigate();
    const feedbackFn = useFeedback();
    const [topOffset, setTopOffset] = useState(-46);
    const listRef = useRef<HTMLUListElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listExistClass = listReverse ? "reverse-list--exist" : "list--exist";
    const [_, setDataView] = useSelectedDataViewName();
    const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
    const [isResetImage, setIsResetImage] = useState(false);
    const { handleResetChat } = useContext(AiAssistContext);


    const esApiKey = useJwtToken();

    const setNewQueryInUrl = (newQuery: string) => {
        const searchParams = new URLSearchParams(location.search);
        if (newQuery) {
            searchParams.set("q", newQuery);
        }
        const queryString = searchParams.toString();
        return queryString ? queryString : null;
    };

    useEffect(() => {
        if (!loading)
            inputRef.current?.focus();
    }, [loading]);

    useEffect(() => {
        console.log({ defaultInputVal })
        setQuery(defaultInputVal || "");
    }, [defaultInputVal]);

    useEffect(() => {
        if (disabledSearch) {
            queryChange && queryChange(query);
            return;
        }
        const delayDebounceFn = setTimeout(() => {
            queryChange && queryChange(query);
            if (query.trim() !== "" && query.length > 1 && indices?.length) {
                switch (source) {
                case "ai-assist":
                    console.log("INDICES LENGTH", indices?.length);
                    if (indices && indices.length > 0) {
                        fetchAdvanceSearchData(
                            query,
                            indices || [],
                            i18n.language,
                            esApiKey,
                        );
                    }
                    break;
                case "all-apps":
                    getAllApps(query, esApiKey);
                    break;
                default:
                    fetchAdvanceSearchData(
                        query,
                        indices || [],
                        i18n.language,
                        esApiKey,
                    );
                }
            }
        }, 500);
        return () => {
            clearTimeout(delayDebounceFn);
            resultUpdate([]);
        };
    }, [query]);

    useEffect(() => {
        setInputFocused(suggestedQuestions.length > 0);
    }, [suggestedQuestions]);

    useLayoutEffect(() => {
        if (listRef.current && listReverse) {
            setTopOffset(
                document.getElementsByClassName("result_ul")[0].clientHeight,
            );
        } else if (!listReverse && enableGrow) {
            const elem = document.getElementsByClassName("search-list-component")[0]
            if (elem) {
                const currentHeight = (elem as HTMLElement).offsetHeight - 1 || 0;
                setTopOffset(-currentHeight);
            }
        }
    });

    const inputSubmit = async (e?: any) => {
        console.log("submitting input")
        console.log("INPUT QUERY", query);
        console.log("source", source)
        if (source == "ai-assist") {
            console.log("going to ai assist");
            navigate(`${location.pathname}?${setNewQueryInUrl(query)}`, { replace: isAiPageAlready });
            if (itemClickedCallback) {
                console.log("CALLING BACK");
                itemClickedCallback(query);
                setQuery("");
            }
            return;
        }
        const page = source === 'all-apps' ? "all-apps" : "simple";
        feedbackFn({ source: "query", page, query, role: currentRole || "unknown" });

        e?.preventDefault();
        await iconClickHandler();
    };

    const iconClickHandler = async () => {
        if (source !== "all-apps" && query) {
            setDataView("all");
            handleResetChat();
            navigate(`/ai`, { replace: isAiPageAlready, state: { query } });
        }
    };


    const onClickOutside = () => {
        setInputFocused(false);
    };

    const listItemClicked = (
        question: string,
        isFromquestionairData?: string,
    ) => {
        setQuery("");
        if (itemClickedCallback) {
            itemClickedCallback(question);
        }
        if (source !== "all-apps" && isFromquestionairData === "questionairData") {
            // need to check if we have to call below function
            // feedbackFn({ source: "query", page: "simple", query: question });
            setDataView("all");
            !isAiPageAlready && handleResetChat();
            navigate(`/ai`, { replace: isAiPageAlready, state: { query: question } });
        }
    };


    useClickOutside("search-input", onClickOutside);
    const listWrapper = useOutsideFocus<HTMLDivElement>(() => {
        setInputFocused(false);
    });

    return (
        <div
            id="search-input"
            ref={listWrapper}
            className={`search-list-component ${isSearchFocused ? "input--focused" : ""
                }
                ${query.length > 1 &&
                    isSearchFocused && (questionairData.length > 0)
                    ? listExistClass
                    : ""
                }`}
        >
            <EonUiForm onFormSubmit={!enableGrow ? inputSubmit : undefined}>
                {enableGrow ? (
                    <AutoResizingTextarea
                        id="tileTour1"
                        setSuggestedQuestions={setSuggestedQuestions}
                        isSearchFocused={isSearchFocused}
                        isReverse={listReverse}
                        isResetImage={isResetImage}
                        setIsResetImage={setIsResetImage}
                        suggestedQuestions={suggestedQuestions}
                        placeholder={placeholder ?? t("searchPage.searchPlaceholder")}
                        inputSubmit={inputSubmit}
                        onFocus={() => setInputFocused(true)}
                        hasSearchData={query.length > 1 && (questionairData.length > 0)}
                        value={query}
                        onChange={(e: any) => setQuery(e.target.value)}
                    />
                ) : (
                    <input
                        ref={inputRef}
                        id="tileTour1"
                        className="input-box-wrapper"
                        type="text"
                        value={query}
                        // onBlur={() => setTimeout(() => {
                        //     dispatch(setInputFocused(false));
                        // }, 500)}
                        autoComplete="off"
                        onFocus={() => setInputFocused(true)}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={placeholder ?? t("searchPage.searchPlaceholder")}
                        title={t("searchPage.searchApps")}
                        disabled={isEditMode}
                    />
                )}
                {loading && (
                    <div className="loader">
                        <EonUiPreloaderCircle size="small" />
                    </div>
                )}
                {!enableGrow && (
                    <button
                        className="search-icon-container"
                        onClick={iconClickHandler}
                        disabled={isEditMode}
                        aria-label={t("searchPage.searchApps")}
                    ></button>
                )}
            </EonUiForm>
            {suggestedQuestions.length > 0 && isSearchFocused && (
                <ul
                    style={{ top: -topOffset }}
                    ref={listRef}
                    className={`result_ul
                        result_ul${limitedHeight ? "--limited-height" : ""}
                        ${listReverse
                            ? "result_ul--reverse"
                            : "result_ul--normal"
                        }
                    `}
                >
                    {suggestedQuestions.length > 0 && (
                        <>
                            {source != "ai-assist" && (
                                <div className="result-search-subheader">
                                    {t("landingPage.suggestions")}
                                </div>
                            )}
                            {suggestedQuestions.map(
                                (
                                    result,
                                    index: number,
                                ) => (
                                    <ListItem
                                        resultData={{ title: result }}
                                        key={`${index + 1}`}
                                        listItemClicked={(question) => {
                                            setSuggestedQuestions([]);
                                            // setInputFocused(false);
                                            setIsResetImage(true);
                                            listItemClicked(
                                                question,
                                                "questionairData",
                                            )
                                        }
                                        }
                                    />
                                ),
                            )}
                        </>
                    )}
                </ul>
            )}
            {
                query.length > 1 &&
                suggestedQuestions.length === 0 &&
                isSearchFocused &&
                (questionairData.length > 0) && (
                    <ul
                        style={{ top: -topOffset }}
                        ref={listRef}
                        className={`result_ul
                        result_ul${limitedHeight ? "--limited-height" : ""}
                        ${listReverse
                                ? "result_ul--reverse"
                                : "result_ul--normal"
                            }
                    `}
                    >
                        {questionairData && questionairData.length > 0 && (
                            <>
                                {questionairData.map(
                                    (
                                        result:
                                            | TileDataInterface
                                            | newAiQue | que,
                                        index: number,
                                    ) => (
                                        <ListItem
                                            resultData={result}
                                            key={`${index + 1}`}
                                            listItemClicked={async (question) => {
                                                queryChange && queryChange(question);
                                                listItemClicked(
                                                    question,
                                                    "questionairData",
                                                )
                                            }
                                            }
                                        />
                                    ),
                                )}
                            </>
                        )}
                    </ul>
                )
            }
        </div>
    );
};

